// Sourced from analysis-service/scorecard_example.json (2026-08-07); that snapshot predates the
// required `status` field, so each dimension below carries the `'ok'` pixee-platform substitutes
// when analysis-service omits it. The analysis-service payload is snake_case; pixee-platform
// projects it into the camelCase wire format the frontend consumes. This module performs the same
// projection at import time so the demo mock can present a realistic completed scorecard the
// moment the tab is opened.
import type {
  DimensionAnswer,
  DimensionAnswerEvidence,
  DimensionScore,
  ScorecardsResponse,
} from '../src/utils/api-client/api-schemas'

type RawEvidence = {
  location: string
  note?: string
  excerpt?: string
}

type RawAnswer = {
  id: string
  question: string
  answer: 'yes' | 'no' | 'na'
  rationale?: string
  evidence?: RawEvidence[]
}

type RawDimension = {
  dimension: string
  display_name: string
  description: string
  score: number
  confidence: number
  rationale: string
  answers: RawAnswer[]
  rubric_version: string
  model_used: string
  generated_at: string
  status: 'ok' | 'failed'
}

type RawScorecard = {
  system_overview: string
  commit_sha: string
  threat_model_doc_id: string
  threat_model_commit_sha: string
  assumptions_overhang: number
  generated_at: string
  dimensions: RawDimension[]
}

const RAW: RawScorecard = {
  system_overview:
    "analysis-service is Pixee's internal FastAPI orchestrator for deterministic and LLM-enabled security fix generation. It ingests code archives via presigned URLs, runs triage and codemod workflows, and returns CodeTF results to pixee-platform. It is not internet-facing; all authentication and org-scoping are enforced by pixee-platform before requests reach this service. Persistence is Postgres via SQLAlchemy/Alembic; concurrency is bounded by an asyncio semaphore on a single uvicorn worker.",
  commit_sha: 'ffc448295a1c2d0e6f7b8a9c0d1e2f3456789abc',
  threat_model_doc_id: '8a3d1e6c-4b7f-4d2a-9e0c-1f2b3c4d5e6f',
  threat_model_commit_sha: 'ffc448295a1c2d0e6f7b8a9c0d1e2f3456789abc',
  assumptions_overhang: 0.28,
  generated_at: '2026-08-07T14:32:11Z',
  dimensions: [
    {
      dimension: 'exposure-adjusted-attack-surface',
      display_name: 'Exposure-Adjusted Attack Surface',
      description:
        "How big and how reachable is the surface an attacker could touch, given the system's exposure and how much of it accepts anonymous callers.",
      score: 0.86,
      confidence: 0.82,
      rationale:
        'The service is not exposed via Ingress — pixee-platform is the only caller — so the effective external attack surface is negligible. The internal API is modest (roughly a dozen route modules under `app.py`) and administrative surface is absent. The small deduction reflects the health endpoints and a handful of routes that accept large presigned-URL payloads without app-layer rate limiting; those are acceptable given the trusted caller but would not be if exposure changed.',
      answers: [
        {
          id: 'perimeter-defended',
          question:
            'Is the system NOT directly internet-facing, OR does it sit behind an ingress/WAF that enforces baseline defense (rate limits, request filtering) before requests reach the app?',
          answer: 'yes',
          rationale:
            'The API docstring in `scorecard/api.py` explicitly notes that nothing here is exposed via Ingress and that auth/org-scoping is enforced upstream at the platform boundary.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/scorecard/api.py:12',
              note: 'Module-level contract stating the service is internal-only.',
              excerpt: 'These endpoints are consumed only by pixee-platform; nothing here is exposed via Ingress.',
            },
          ],
        },
        {
          id: 'anonymous-surface-minimal',
          question:
            'Is the anonymous-reachable surface limited to a small, deliberate set of endpoints (e.g., login, health, public catalog) rather than sprawling across the API?',
          answer: 'yes',
          rationale:
            'Only `/health` is intended for anonymous callers; every other route is scoped to pixee-platform via network-level trust.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/health.py',
              note: 'Sole endpoint contract that would be reachable without upstream auth.',
            },
          ],
        },
        {
          id: 'dangerous-endpoints-gated',
          question:
            'Are dangerous endpoint kinds (file uploads, webhooks, deserialization endpoints, RPC) either absent, or gated with authentication and size/type/rate limits?',
          answer: 'no',
          rationale:
            'Code archives arrive as presigned URLs the service downloads and unzips; there is size validation but no explicit per-caller rate limit inside the service. Acceptable in the current threat model but a soft spot if the trust boundary shifts.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/workflow/task_queue.py',
              note: 'Ingest path pulls remote archives; relies on upstream caller trust for throttling.',
            },
          ],
        },
        {
          id: 'admin-surface-segmented',
          question:
            'Is administrative surface (/admin/*, /internal/*, /debug/*) reachable only from authenticated privileged actors — never from anonymous callers?',
          answer: 'na',
          rationale: 'The service exposes no `/admin`, `/internal`, or `/debug` routes.',
          evidence: [],
        },
        {
          id: 'entry-point-count-reasonable',
          question:
            "Is the number of distinct entry points modest relative to the system's stated purpose, without duplicate or forgotten endpoints inflating the surface?",
          answer: 'yes',
          rationale:
            'Endpoints under `app.py` map 1:1 to workflow types (analysis, threat models, scorecards, exploit chains, finding overlays); no legacy duplicates observed.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/app.py',
              note: 'Single registration site for all API routers.',
            },
          ],
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:42Z',
      status: 'ok',
    },
    {
      dimension: 'defense-in-depth-coverage',
      display_name: 'Defense-in-Depth Coverage',
      description:
        'Whether the codebase has an appropriate breadth of security controls layered across the request path, rather than relying on a single gate.',
      score: 0.62,
      confidence: 0.74,
      rationale:
        'The service leans heavily on pixee-platform for perimeter controls, which is deliberate but concentrates risk. In-process controls that do exist — semaphore-bounded concurrency, backpressure decisions, structured logging with request IDs — are consistent. Missing layers are app-level input validation on presigned URLs beyond Pydantic type coercion and any per-tenant quota enforcement.',
      answers: [
        {
          id: 'layered-input-validation',
          question:
            'Is user-supplied input validated at more than one layer (schema + business rule + storage constraints)?',
          answer: 'yes',
          rationale:
            'Pydantic models validate request shape; workflow-layer checks reject unknown tools, missing threat models, and stale commit SHAs.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/workflow/models.py',
              note: 'Shared Pydantic request/response models used across endpoints.',
            },
          ],
        },
        {
          id: 'backpressure-and-quotas',
          question: 'Does the service protect itself from overload with backpressure, quotas, or circuit breakers?',
          answer: 'yes',
          rationale:
            'Explicit `BackpressureDecision` return type gates task acceptance; `asyncio.Semaphore` bounds concurrent workflows.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/workflow/backpressure.py',
              note: 'First-class backpressure primitive used by every submit endpoint.',
            },
          ],
        },
        {
          id: 'safe-defaults',
          question:
            'Do configuration defaults favor security (deny-by-default, feature flags off, minimal permissions)?',
          answer: 'yes',
          rationale:
            'Settings in `conf.py` default to strict values (LLM providers explicit, MLflow tracking off by default, no wildcard tool enablement).',
          evidence: [
            {
              location: 'src/pixee_analysis_service/conf.py',
              note: 'Central settings surface; opt-in for external integrations.',
            },
          ],
        },
        {
          id: 'internal-abuse-controls',
          question:
            'Are there controls that would catch a compromised or misbehaving upstream caller (per-tenant rate limits, anomaly detection)?',
          answer: 'no',
          rationale:
            'No per-caller quota inside this service; a hostile upstream could saturate the single uvicorn worker until the semaphore rejects. Backpressure is global, not per-tenant.',
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:44Z',
      status: 'ok',
    },
    {
      dimension: 'blast-radius-containment',
      display_name: 'Blast-Radius Containment',
      description:
        'If a bug were exploited, how well the system contains the damage — sandboxing, least privilege, isolation of tenants and workloads.',
      score: 0.71,
      confidence: 0.7,
      rationale:
        'Workflows run in-process on a shared uvicorn worker, so a memory-corruption or logic bug in one workflow can affect others until the pod restarts. However, per-request code archives are unpacked into isolated temp directories, database access is scoped through a session factory, and outbound LLM calls go through explicit provider clients. Containment is strong laterally but weak temporally within a single process.',
      answers: [
        {
          id: 'tenant-isolation',
          question: 'Is per-request/per-tenant state isolated so a bug in one request cannot leak state into another?',
          answer: 'yes',
          rationale:
            'Each request receives its own SQLAlchemy session and temp working directory; no request-scoped module globals observed.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/database/db.py',
              note: 'Session factory scopes DB access per request.',
            },
          ],
        },
        {
          id: 'least-privilege-secrets',
          question:
            'Does the process run with the minimum credentials it needs (no unused cloud roles, no admin DB user)?',
          answer: 'yes',
          rationale:
            'Runtime configuration surfaces only the credentials required for LLM providers, object storage, and Postgres.',
        },
        {
          id: 'sandboxed-execution',
          question: 'Is untrusted code execution (user-supplied code, LLM tool calls) sandboxed away from the host?',
          answer: 'no',
          rationale:
            "Codemod runs execute against uploaded code in the service's own temp directory without a container/OS-level sandbox; relies on process-level isolation from Kubernetes.",
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:46Z',
      status: 'ok',
    },
    {
      dimension: 'authentication-posture',
      display_name: 'Authentication Posture',
      description:
        'How well access is gated at the perimeter — mechanism strength, credential handling, session lifetime.',
      score: 0.55,
      confidence: 0.6,
      rationale:
        'The service intentionally offloads authentication to pixee-platform; there is no in-process auth middleware. The score reflects the risk that the trust boundary is entirely network-scoped — if the platform gate is bypassed, every endpoint is anonymous-reachable. This is documented and deliberate but the assessment can only rate what the service itself does, which is nothing.',
      answers: [
        {
          id: 'auth-required-by-default',
          question:
            'Do endpoints require authentication by default, with public endpoints being the explicit exception?',
          answer: 'no',
          rationale: 'The service exposes no auth middleware and treats all callers as trusted platform traffic.',
        },
        {
          id: 'auth-mechanism-strength',
          question:
            'Are supported auth mechanisms modern (OIDC, mTLS, signed JWTs with short TTLs) rather than legacy shared secrets?',
          answer: 'na',
          rationale: 'No auth is performed here.',
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:48Z',
      status: 'ok',
    },
    {
      dimension: 'authorization-posture',
      display_name: 'Authorization Posture',
      description: 'Once authenticated, whether access is enforced per-resource and consistently across code paths.',
      score: 0.48,
      confidence: 0.65,
      rationale:
        'As with authentication, org-scoping is enforced upstream by pixee-platform. Inside the service, request payloads carry a `repository_id` that is used as-is; there is no in-process check that the caller is authorized for that repo. If the perimeter were bypassed, an attacker could enumerate repositories by ID.',
      answers: [
        {
          id: 'resource-level-authz',
          question:
            'Is authorization checked at the resource level (per repository, per document, per finding) rather than only at the endpoint level?',
          answer: 'no',
          rationale: '`repository_id` is trusted as passed; no per-resource ACL check in-process.',
        },
        {
          id: 'authz-consistent-across-paths',
          question:
            'Is the same authorization rule applied to every path that touches a resource (API, background workers, admin scripts)?',
          answer: 'na',
          rationale: 'No in-process authz to audit for consistency.',
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:50Z',
      status: 'ok',
    },
    {
      dimension: 'input-handling-hygiene',
      display_name: 'Input Handling Hygiene',
      description:
        'How defensively untrusted input is handled end-to-end — validation, canonicalization, and safe use in downstream sinks.',
      score: 0.79,
      confidence: 0.76,
      rationale:
        'All external inputs are Pydantic-validated at the boundary and passed as typed values through the workflow layer. SQL uses SQLAlchemy ORM exclusively (no raw string interpolation observed). The main soft spot is code archives: filenames from uploaded zips are trusted for path construction, which is a known zip-slip risk category. The service extracts into temp dirs, but path-traversal defenses could not be positively confirmed.',
      answers: [
        {
          id: 'typed-boundary-validation',
          question: 'Is every external input validated with a typed schema at the boundary?',
          answer: 'yes',
          rationale:
            'All FastAPI endpoints use Pydantic request models; no raw dict handling observed at the boundary.',
        },
        {
          id: 'safe-sinks',
          question: 'Are dangerous sinks (SQL, shell, filesystem, template rendering) reached only through safe APIs?',
          answer: 'yes',
          rationale:
            'DB access is ORM-only; subprocess use is via `asyncio.create_subprocess_exec` with argument lists (no shell=True).',
        },
        {
          id: 'archive-extraction-safety',
          question:
            'When extracting archives or handling paths from external sources, is the code defended against traversal and symlink attacks?',
          answer: 'no',
          rationale:
            'Code archive extraction was not confirmed to validate entries against a canonicalized destination root.',
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:52Z',
      status: 'ok',
    },
    {
      dimension: 'secrets-and-credentials-handling',
      display_name: 'Secrets and Credentials Handling',
      description:
        'How safely secrets and credentials are acquired, stored, and used — never in code, never in logs, rotated on schedule.',
      score: 0.83,
      confidence: 0.78,
      rationale:
        'Secrets flow in through environment/settings via `conf.py`; no committed keys were found in the working tree. LLM provider credentials are held in memory only and passed to provider clients. Log output uses structured fields and does not appear to echo secrets, though a systematic audit would be needed to guarantee that.',
      answers: [
        {
          id: 'no-secrets-in-source',
          question: 'Are secrets absent from source control and configuration files that ship with the repo?',
          answer: 'yes',
          rationale:
            'No committed keys or plaintext credentials observed in `conf.py`, docker-compose files, or Helm-consumable configuration.',
        },
        {
          id: 'no-secrets-in-logs',
          question: 'Do log statements avoid leaking secrets, tokens, or credential-bearing URLs?',
          answer: 'yes',
          rationale: 'Presigned URLs are logged only by ID/status; provider credentials are not logged.',
        },
        {
          id: 'central-secret-loading',
          question: 'Is secret loading centralized so rotation and audit are practical?',
          answer: 'yes',
          rationale:
            '`conf.py` is the single settings surface; providers read from it rather than reading env directly.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/conf.py',
              note: 'Single source-of-truth for runtime settings and credentials.',
            },
          ],
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:54Z',
      status: 'ok',
    },
    {
      dimension: 'dependency-risk-posture',
      display_name: 'Dependency Risk Posture',
      description:
        'How well the codebase controls the risk it inherits from third-party dependencies — pinning, review, and upgrade discipline.',
      score: 0.74,
      confidence: 0.8,
      rationale:
        'Dependencies are pinned via `uv.lock`, Renovate is configured to open upgrade PRs, and recent commits show consistent responsiveness to LLM SDK updates. The service pulls in a large surface of AI SDKs (Anthropic, Bedrock, MLflow, pydantic-ai) whose transitive footprint is significant; no evidence of SBOM emission or supply-chain attestation.',
      answers: [
        {
          id: 'pinned-dependencies',
          question: 'Are runtime dependencies fully pinned with a lockfile committed to source control?',
          answer: 'yes',
          rationale: '`uv.lock` is committed and enforced by pre-commit; direct deps are declared in `pyproject.toml`.',
        },
        {
          id: 'automated-upgrade-pipeline',
          question: 'Is there an automated pipeline that opens dependency upgrade PRs?',
          answer: 'yes',
          rationale: '`renovate.json` present with active configuration.',
        },
        {
          id: 'sbom-and-attestation',
          question: 'Does the release process produce an SBOM or supply-chain attestation?',
          answer: 'no',
          rationale: 'No SBOM generation observed in `Makefile` or Dockerfile.',
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:56Z',
      status: 'ok',
    },
    {
      dimension: 'transport-and-network-security',
      display_name: 'Transport and Network Security',
      description:
        'Whether data in motion is protected and internal-network trust is warranted by the controls in place.',
      score: 0.68,
      confidence: 0.62,
      rationale:
        'Outbound calls to LLM providers and object storage use HTTPS clients by default. Inbound is HTTP inside the cluster with TLS terminated at the ingress; this is the standard pattern but means an attacker on the pod network sees plaintext traffic. No mTLS between analysis-service and pixee-platform was observed.',
      answers: [
        {
          id: 'tls-outbound',
          question: 'Are all outbound calls to external systems over TLS?',
          answer: 'yes',
          rationale:
            'Provider SDKs (Anthropic, Bedrock, MLflow) default to HTTPS endpoints and no plaintext overrides were found.',
        },
        {
          id: 'mtls-internal',
          question: 'Is service-to-service traffic inside the cluster authenticated (mTLS or equivalent)?',
          answer: 'no',
          rationale: 'No mTLS or service-mesh identity verification observed in the app layer.',
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:31:58Z',
      status: 'ok',
    },
    {
      dimension: 'observability-and-detection',
      display_name: 'Observability and Detection',
      description:
        'Whether the system can detect and investigate abuse — structured logs, metrics, traces, and alertable signals.',
      score: 0.81,
      confidence: 0.83,
      rationale:
        'The service emits OpenTelemetry metrics via `metrics.py`, structured logs through a central logger, and per-request IDs for correlation. Workflow progress is tracked and streamed via SSE. The gap is on the detection side: no in-app signals wired to fire on abuse patterns (e.g., repeated backpressure rejections from a single caller).',
      answers: [
        {
          id: 'structured-logging',
          question: 'Do logs use structured fields and a consistent request/correlation identifier?',
          answer: 'yes',
          rationale: 'Central logger in `utils/logging.py`; per-request IDs propagated through workflow handlers.',
          evidence: [
            {
              location: 'src/pixee_analysis_service/utils/logging.py',
              note: 'Single logger surface enforced by CLAUDE.md convention.',
            },
          ],
        },
        {
          id: 'metrics-emitted',
          question: 'Are latency, error-rate, and saturation metrics emitted for the primary code paths?',
          answer: 'yes',
          rationale: '`metrics.py` wires OpenTelemetry counters/histograms for workflow lifecycle events.',
        },
        {
          id: 'abuse-signals',
          question:
            'Are there alertable signals for abuse (repeated auth failures, backpressure spikes from one caller, anomalous payload sizes)?',
          answer: 'no',
          rationale:
            'Metrics exist but no explicit abuse-detection signals or per-caller aggregations were identified.',
        },
      ],
      rubric_version: 'v1',
      model_used: 'claude-opus-4-7',
      generated_at: '2026-08-07T14:32:00Z',
      status: 'ok',
    },
  ],
}

const toEvidence = (raw: RawEvidence): DimensionAnswerEvidence => ({
  location: raw.location,
  note: raw.note,
  excerpt: raw.excerpt,
})

const toAnswer = (raw: RawAnswer): DimensionAnswer => ({
  id: raw.id,
  question: raw.question,
  answer: raw.answer,
  rationale: raw.rationale,
  evidence: (raw.evidence ?? []).map(toEvidence),
})

const toDimension = (raw: RawDimension): DimensionScore => ({
  dimension: raw.dimension,
  displayName: raw.display_name,
  description: raw.description,
  score: raw.score,
  confidence: raw.confidence,
  rationale: raw.rationale,
  answers: raw.answers.map(toAnswer),
  rubricVersion: raw.rubric_version,
  modelUsed: raw.model_used,
  generatedAt: raw.generated_at,
  status: raw.status,
})

export const EXAMPLE_SCORECARDS_RESPONSE: ScorecardsResponse = {
  status: 'completed',
  phase: 'finalizing',
  generatedAt: RAW.generated_at,
  requestedBy: 'demo@pixee.ai',
  commitSha: RAW.commit_sha,
  scorecard: {
    systemOverview: RAW.system_overview,
    commitSha: RAW.commit_sha,
    threatModelDocId: RAW.threat_model_doc_id,
    threatModelCommitSha: RAW.threat_model_commit_sha,
    assumptionsOverhang: RAW.assumptions_overhang,
    generatedAt: RAW.generated_at,
    dimensions: RAW.dimensions.map(toDimension),
  },
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/scorecards' },
  },
}
